import API from '../api';
import type {
  CompanySize,
  HonoraryFlagValue,
  AccountRegistrationStatus,
  SetAccountParentDto,
  UpdateCompanyProfileDto,
  Address,
} from '@paladin-monorepo/common';

export interface AccountListItem {
  id: string;
  accountType: string;
  displayName?: string | null;
  defaultCurrency?: string;
  companyIndustryId?: string | null;
  companySize?: CompanySize | null;
  companyVerifiedAt?: string | null;
  honoraryFlag?: HonoraryFlagValue | null;
  prince2Enabled?: boolean;
  bedrockEnabled?: boolean;
  parentAccountId?: string | null;
  escalateToParent?: boolean;
  registrationStatus?: AccountRegistrationStatus;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  party?: {
    id?: string;
    name?: string;
    companyId?: string;
    vatId?: string | null;
    linkedinUrl?: string | null;
    address?: Address;
    ico?: string;
  };
  memberships?: Array<{
    id: string;
    role: string;
    blockedAt?: string | null;
    verifiedAt?: string | null;
    user: {
      id: string;
      email: string;
      verifiedAt?: string | null;
      party?: {
        id?: string;
        firstName?: string;
        lastName?: string;
      };
    };
  }>;
}

export const accountsApi = {
  async list(): Promise<AccountListItem[]> {
    return API.get<AccountListItem[]>('/account').then((res) => res.data);
  },
  async get(id: string): Promise<AccountListItem> {
    return API.get<AccountListItem>(`/account/${id}`).then((res) => res.data);
  },
  async getSuppliers(): Promise<AccountListItem[]> {
    return API.get<AccountListItem[]>('/account/suppliers').then((res) => res.data);
  },
  async update(
    id: string,
    data: {
      displayName?: string | null;
      defaultCurrency?: string;
      companyIndustryId?: string | null;
      companySize?: CompanySize | null;
    },
  ): Promise<AccountListItem> {
    return API.patch<AccountListItem>(`/account/${id}`, data).then((res) => res.data);
  },

  async updateCompany(
    id: string,
    data: UpdateCompanyProfileDto,
  ): Promise<AccountListItem> {
    return API.patch<AccountListItem>(`/account/${id}/company`, data).then((res) => res.data);
  },

  async verifyInquirerCompany(id: string): Promise<AccountListItem> {
    return API.post<AccountListItem>(`/account/${id}/verify-company`).then((res) => res.data);
  },

  async unverifyInquirerCompany(id: string): Promise<AccountListItem> {
    return API.delete<AccountListItem>(`/account/${id}/verify-company`).then((res) => res.data);
  },

  async setHonoraryFlag(
    id: string,
    honoraryFlag: HonoraryFlagValue | null,
  ): Promise<AccountListItem> {
    return API.patch<AccountListItem>(`/account/${id}/honorary-flag`, { honoraryFlag }).then(
      (res) => res.data,
    );
  },

  async setPrince2Enabled(id: string, prince2Enabled: boolean): Promise<AccountListItem> {
    return API.patch<AccountListItem>(`/account/${id}/prince2`, { prince2Enabled }).then(
      (res) => res.data,
    );
  },
  async setBedrockEnabled(id: string, bedrockEnabled: boolean): Promise<AccountListItem> {
    return API.patch<AccountListItem>(`/account/${id}/bedrock`, { bedrockEnabled }).then(
      (res) => res.data,
    );
  },
  async setParentAccount(id: string, dto: SetAccountParentDto): Promise<AccountListItem> {
    return API.patch<AccountListItem>(`/account/${id}/parent`, dto).then((res) => res.data);
  },

  async approveRegistration(id: string): Promise<AccountListItem> {
    return API.post<AccountListItem>(`/account/${id}/approve`).then((res) => res.data);
  },

  async rejectRegistration(id: string): Promise<AccountListItem> {
    return API.post<AccountListItem>(`/account/${id}/reject`).then((res) => res.data);
  },
};


